import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Search, AccountTree } from '@mui/icons-material';
import { useCases } from '../../contexts/api-contexts/CasesContext';
import { Symptom, PaginatedResponse } from '../../types/api.types';
import { gekkoApi } from '../../api/GekkoApi';

interface CaseSymptomSelectionDialogProps {
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`symptom-selection-tabpanel-${index}`}
      aria-labelledby={`symptom-selection-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `symptom-selection-tab-${index}`,
    'aria-controls': `symptom-selection-tabpanel-${index}`,
  };
}

const CaseSymptomSelectionDialog: React.FC<CaseSymptomSelectionDialogProps> = ({
  open,
  onClose,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<Symptom[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const { currentCase, setCaseProperty } = useCases();

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Reset state when changing tabs
    setSearchText('');
    setSearchResults([]);
    setSearchError(null);
  };

  // Search symptoms with debouncing
  const searchSymptoms = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setSearchError(null);

    try {
      // Use gekkoApi directly to avoid modifying the shared symptoms context
      const response = await gekkoApi.get<PaginatedResponse<Symptom>>(
        '/symptoms',
        {
          queryParams: { q: query, page_size: 50 },
        }
      );
      setSearchResults(response.items || []);
    } catch (error) {
      console.error('Error searching symptoms:', error);
      setSearchError('Failed to search symptoms. Please try again.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchText.trim()) {
        searchSymptoms(searchText.trim());
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchText, searchSymptoms]);

  // Handle adding symptom to case
  const handleAddSymptom = useCallback(
    (symptom: Symptom) => {
      if (!currentCase) return;

      // Check if symptom is already selected
      const isAlreadySelected = currentCase.selected_symptoms?.some(
        (selected) => selected.id === symptom.id
      );

      if (isAlreadySelected) {
        setSearchError('This symptom is already selected.');
        return;
      }

      const updatedSelectedSymptoms = [
        ...(currentCase.selected_symptoms || []),
        { id: symptom.id, significant: false },
      ];

      // Remove from deselected if it was there
      const updatedDeSelectedSymptoms =
        currentCase.de_selected_symptoms?.filter((id) => id !== symptom.id) ||
        [];

      setCaseProperty('selected_symptoms', updatedSelectedSymptoms);
      setCaseProperty('de_selected_symptoms', updatedDeSelectedSymptoms);

      // Reset selection and show success feedback
      setSearchText('');
      setSearchResults([]);

      // Close dialog after adding
      onClose();
    },
    [currentCase, setCaseProperty, onClose]
  );

  // Helper function to get symptom display name
  const getSymptomDisplayName = (symptom: Symptom): [string, string] => {
    let name = '';
    if (symptom.parent_symptom) {
      name = `${symptom.parent_symptom.name}, `;
    }
    name += symptom.name;

    let typeName = '';
    if (symptom.symptom_type) {
      typeName = `${symptom.symptom_type.category.name}, ${symptom.symptom_type.name}`;
    }
    return [name, typeName];
  };

  // Handle dialog close
  const handleClose = () => {
    setTabValue(0);
    setSearchText('');
    setSearchResults([]);
    setSearchError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="symptom-selection-dialog-title"
    >
      <DialogTitle id="symptom-selection-dialog-title">
        Add Symptom to Case
      </DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="symptom selection tabs"
          >
            <Tab
              icon={<Search />}
              label="Search Symptoms"
              {...a11yProps(0)}
              iconPosition="start"
            />
            <Tab
              icon={<AccountTree />}
              label="Browse Tree"
              {...a11yProps(1)}
              iconPosition="start"
              disabled
            />
          </Tabs>
        </Box>

        {/* Search Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search for symptoms by name. Type at least 2 characters to see
              results.
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              placeholder="Start typing symptom name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                endAdornment: searchLoading && <CircularProgress size={20} />,
              }}
            />
          </Box>

          {searchError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {searchError}
            </Alert>
          )}

          {searchText.length > 0 && searchText.length < 2 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 2 }}
            >
              Type at least 2 characters to search
            </Typography>
          )}

          {searchText.length >= 2 &&
            !searchLoading &&
            searchResults.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 2 }}
              >
                No symptoms found matching &quot;{searchText}&quot;
              </Typography>
            )}

          {searchResults.length > 0 && (
            <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
              {searchResults.map((symptom) => {
                const [name, typeName] = getSymptomDisplayName(symptom);
                const isAlreadySelected = currentCase?.selected_symptoms?.some(
                  (selected) => selected.id === symptom.id
                );

                return (
                  <ListItem
                    key={symptom.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 0.5,
                      bgcolor: isAlreadySelected
                        ? 'action.selected'
                        : 'background.paper',
                      opacity: isAlreadySelected ? 0.7 : 1,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          {name}
                          {isAlreadySelected && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                bgcolor: 'success.light',
                                color: 'success.contrastText',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                              }}
                            >
                              Selected
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={typeName}
                      primaryTypographyProps={{
                        variant: 'body1',
                        component: 'div',
                        color: isAlreadySelected
                          ? 'text.secondary'
                          : 'text.primary',
                      }}
                      secondaryTypographyProps={{
                        variant: 'body2',
                        color: isAlreadySelected
                          ? 'text.secondary'
                          : 'text.secondary',
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleAddSymptom(symptom)}
                        disabled={isAlreadySelected}
                        size="small"
                        sx={{
                          color: isAlreadySelected
                            ? 'text.disabled'
                            : 'success.main',
                        }}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </TabPanel>

        {/* Tree Browse Tab (Placeholder) */}
        <TabPanel value={tabValue} index={1}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Symptom tree browsing is not yet available. Please use the search
            function for now.
          </Alert>

          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AccountTree sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Tree Browsing Coming Soon
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This feature will allow you to browse symptoms organized by
              category and type in a hierarchical tree structure.
            </Typography>
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaseSymptomSelectionDialog;
